import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function StudentLedgerReport() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nationality: { value: "All", label: "All" },
      section: { value: "all", label: "All" },
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [ledgerData, setLedgerData] = useState([]);
  const [filteredLedgerData, setFilteredLedgerData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const nationalityOptions = [
    { value: "All", label: "All" },
    { value: "Saudi", label: "Saudi" },
    { value: "Non-Saudi", label: "Non-Saudi" },
  ];

  // Function to format date to DD/MM/YY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    const yy = year.slice(-2);
    return `${day}/${month}/${yy}`;
  };

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCentersData(response);
        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
            setCenterId(selectedCenter.id);
            fetchSectionsByCenterId(selectedCenter.id);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
        toast.error("Failed to fetch centers");
      }
    };
    fetchCenters();
  }, []);

  const fetchSectionsByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const yearGroupsResponse = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const yearGroups = yearGroupsResponse;

        const sectionsPromises = yearGroups.map((yearGroup) =>
          get(
            `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${yearGroup.id}&centerId=${centerId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );

        const sectionsResponses = await Promise.all(sectionsPromises);
        const allSections = sectionsResponses.flatMap((response) => response);
        setSectionsData(allSections);
      } else {
        setSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Failed to fetch sections");
    }
  };

  const fetchLedgerData = async () => {
    const centerId = watch("centerId")?.value;
    const sectionValue = watch("section")?.value;
    const nationality = watch("nationality")?.value;
    const fromDate = watch("fromDate");
    const toDate = watch("toDate");

    if (!centerId) {
      setLedgerData([]);
      setFilteredLedgerData([]);
      return;
    }

    const body = {
      centerId: parseInt(centerId),
      rollNo: "",
      classId: 0,
      sectionId: sectionValue === "all" ? 0 : parseInt(sectionValue),
      isSaudi: { All: 0, Saudi: 1, "Non-Saudi": 2 }[nationality] || 0,
      fromDate: fromDate || null,
      toDate: toDate || null,
    };
    try {
      const response = await post(
        "Reports/StudentLedgerReportByCenterClassandRegistartionNoAsync",
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const center = centersData.find((c) => c.id === centerId);
      const centerName = center ? center.name : "N/A";

      const enrichedData = (response || []).map((entry) => ({
        ...entry,
        centerName: entry.centerName || centerName,
        className: entry.className || "All",
      }));

      setLedgerData(enrichedData);
      setFilteredLedgerData(enrichedData);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      toast.error("Failed to fetch ledger data");
    }
  };

  useEffect(() => {
    fetchLedgerData();
  }, [
    watch("centerId"),
    watch("section"),
    watch("nationality"),
    watch("fromDate"),
    watch("toDate"),
  ]);

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      width: "60px",
    },
    {
      name: "Full Name",
      cell: (row) => {
        let imageSrc = "";
        if (!row.studentImage || row.studentImage === "string") {
          imageSrc = `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`;
        } else {
          imageSrc = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages/${row.imagePath}`;
        }
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="table-listing-image-preview">
              <img src={imageSrc} alt="Image" />
            </div>
            <div>{row.firstName + " " + row.lastName}</div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Registration No",
      selector: (row) => row.registrationNo || "N/A",
      sortable: true,
    },
    {
      name: "Year Group",
      selector: (row) => row.className || "N/A",
      sortable: true,
    },
    {
      name: "Date of Birth",
      selector: (row) => formatDate(row.dateOfBirth),
      sortable: true,
    },
    {
      name: "Nationality",
      selector: (row) => row.nationality || "N/A",
      sortable: true,
    },
    {
      name: "Challan ID",
      selector: (row) => row.challanId || "N/A",
      sortable: true,
    },
    {
      name: "Challan Month",
      selector: (row) => row.challanMonth || "N/A",
      sortable: true,
    },
    {
      name: "Challan Date",
      selector: (row) => formatDate(row.challanDate),
      sortable: true,
    },
    {
      name: "Challan Due Date",
      selector: (row) => formatDate(row.challanDueDate),
      sortable: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalAmount || "0",
      sortable: true,
    },
    {
      name: "VAT",
      selector: (row) => row.vat || "0",
      sortable: true,
    },
    {
      name: "Net Amount",
      selector: (row) => row.netAmount || "0",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.isPaid ? "Paid" : "Un Paid"),
      sortable: true,
    },
    {
      name: "Receipt No",
      selector: (row) => row.receiptNo || "N/A",
      sortable: true,
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks || "N/A",
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const data = filteredLedgerData.map((entry, index) => ({
      "Sr No": (currentPage - 1) * rowsPerPage + index + 1,
      "Full Name": `${entry.firstName || ""} ${entry.lastName || ""}` || "N/A",
      "Registration No": entry.registrationNo || "N/A",
      "Year Group": entry.className || "N/A",
      "Date of Birth": formatDate(entry.dateOfBirth),
      Nationality: entry.nationality || "N/A",
      "Challan ID": entry.challanId || "N/A",
      "Challan Month": entry.challanMonth || "N/A",
      "Challan Date": formatDate(entry.challanDate),
      "Challan Due Date": formatDate(entry.challanDueDate),
      "Total Amount": entry.totalAmount || "0",
      VAT: entry.vat || "0",
      "Net Amount": entry.netAmount || "0",
      Status: entry.isPaid ? "Paid" : "Un Paid",
      "Receipt No": entry.receiptNo || "N/A",
      Remarks: entry.remarks || "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");
    XLSX.writeFile(workbook, "StudentLedgerReport.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Student Ledger Report";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const pdfColumnNames = [
      "Sr No.",
      "Full Name",
      "Registration No",
      "Year Group",
      "Date of Birth",
    ];

    const bodyData = filteredLedgerData.map((entry, index) => [
      (currentPage - 1) * rowsPerPage + index + 1,
      `${entry.firstName || ""} ${entry.lastName || ""}` || "N/A",
      entry.registrationNo || "N/A",
      entry.className || "N/A",
      formatDate(entry.dateOfBirth),
    ]);

    doc.autoTable({
      head: [pdfColumnNames],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });
    doc.save("StudentLedgerReport.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Student Ledger Report</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">
                Center<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="centerId"
                control={control}
                rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={centersData.map((center) => ({
                      value: center.id,
                      label: center.name,
                    }))}
                    isDisabled={savedCenterId !== "0"}
                    onChange={(selectedOption, actionMeta) => {
                      if (actionMeta.action === "clear") {
                        setValue("centerId", null);
                        setValue("section", { value: "all", label: "All" });
                        setCenterId(null);
                        setSectionsData([]);
                        setLedgerData([]);
                        setFilteredLedgerData([]);
                      } else {
                        setValue("centerId", selectedOption);
                        setValue("section", { value: "all", label: "All" });
                        setCenterId(selectedOption.value);
                        fetchSectionsByCenterId(selectedOption.value);
                      }
                      trigger("centerId");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
              {errors.centerId && (
                <small className="text-danger">{errors.centerId.message}</small>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label">Section</label>
              <Controller
                name="section"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={[
                      { value: "all", label: "All" },
                      ...sectionsData.map((section) => ({
                        value: section.id,
                        label: section.name,
                      })),
                    ]}
                    onChange={(selectedOption) => {
                      setValue("section", selectedOption);
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">From Date</label>
              <Controller
                name="fromDate"
                control={control}
                render={({ field }) => (
                  <input type="date" className="form-control" {...field} />
                )}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">To Date</label>
              <Controller
                name="toDate"
                control={control}
                render={({ field }) => (
                  <input type="date" className="form-control" {...field} />
                )}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">
                Nationality<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="nationality"
                control={control}
                rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={nationalityOptions}
                    onChange={(selectedOption) => {
                      setValue("nationality", selectedOption);
                      trigger("nationality");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                  />
                )}
              />
              {errors.nationality && (
                <small className="text-danger">
                  {errors.nationality.message}
                </small>
              )}
            </div>
            <div className="col">
              <label className="form-label" style={{ visibility: "hidden" }}>
                Action
              </label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to Excel"
                  className="btn btn-outline-success px-2 py-1"
                  onClick={exportToExcel}
                >
                  <i className="fs-8 fa fa-file-excel"></i>
                </button>
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to PDF"
                  className="btn btn-outline-danger px-2 py-1"
                  onClick={exportToPDF}
                >
                  <i className="fs-8 fa fa-file-pdf"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-body">
          <DataTable
            columns={columns}
            data={filteredLedgerData}
            pagination
            dense
            highlightOnHover={true}
            pointerOnHover={true}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newRowsPerPage) =>
              setRowsPerPage(newRowsPerPage)
            }
          />
        </div>
      </div>
    </>
  );
}

export default StudentLedgerReport;
